import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { Box } from '@mui/system';
import { StyledPageTitle } from 'theme/styles';
import { EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { Resolver, useForm } from 'react-hook-form';
import { LocationMap } from 'components/organisms/LocationMap';
import { useLocation, useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import { useLicenceContext } from 'models/licenceContext';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAlert } from 'models/alertContext';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { ActionTypes } from 'state/actions/alert';
import { Table } from 'components/molecules/Table';
import { renderRowColor, renderRowColorHover } from 'components/molecules/Table/helpers';
import { parseDictionary } from 'helpers/dictionary';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { isListNotEmpty } from 'utils/listUtils';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { CardList } from 'components/molecules/CardList';
import { ITableDataSource } from 'models/table';
import {
  mapLEAddressToDataSource,
  MovedTransactionTableColumns,
} from 'pages/TransactionsPage/MovedTransactionPage/helpers';
import { formatDateTimeToIso } from 'utils/date';
import {
  createAncillaryMiddleTransaction,
  createMiddleTransaction,
  createMiddleTransactionForAncillaryGroup,
  createMiddleTransactionForGroup,
  getAncillaryGroupLastTransaction,
  getAncillaryLastTransaction,
  getFirearmLastTransaction,
  getGroupLastTransaction,
} from 'requests/transaction';
import { Asset } from 'utils/assets';
import { ETransactionTypes } from 'constants/TransactionTypes';
import { MovedTransactionAddressTableCard } from 'pages/TransactionsPage/MovedTransactionPage/AddressTableCard';
import { FormDateTimePicker } from 'components/molecules/FormDateTimePicker';
import { isStringEmpty } from 'utils/string';
import { ILegalEntityExtendedAddress } from 'models/legalEntity';

const initialFormValues = {
  transactionDate: '',
};

const MovedTransactionPage = () => {
  const [coords, setCoords] = useState<any>(null);
  const { state: routerState }: any = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { isOnlineLicence } = useLicenceContext();
  const { clearAlert, setAlert, samePageAlert } = useAlert();
  const [selectedAddress, setSelectedAddress] = useState<ITableDataSource | null>(null);
  const { region, country, addressType, getRegion, getCountry, getAddressType } =
    useDictionary();
  const { selectedLanguage } = useLang();
  const { getLegalEntityStorageAddressesData, legalEntityStorageAddress } = useLegalEntities();
  const { isDesktop } = useThemeBreakpoint();
  const [lastTransactionUniqueId, setLastTransactionUniqueId] = useState<string | null>();
  const [ownerUniqueId, setOwnerUniqueId] = useState<string | undefined>(undefined);
  const [keeperUniqueId, setKeeperUniqueId] = useState<string | undefined>(undefined);
  const [previousCoords, setPreviousCoords] = useState<any>(null);
  const [legalEntityFilteredStorageAddresses, setLegalEntityFilteredStorageAddresses] =
    useState<ILegalEntityExtendedAddress[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [createTransactionError, setCreateTransactionError] = useState<string>('');

  const schema = yup.object().shape({
    transactionDate: yup
      .string()
      .required(t('general.field.validation.required'))
      .test(
        'date-validation',
        t('legalEntity.validation.maxDate').replace('{0}', '255'),
        (value) => {
          if (typeof value !== 'string') {
            return false;
          }
          const maxValidDate = new Date();
          const passedDate = new Date(value);
          return passedDate.valueOf() <= maxValidDate.valueOf();
        },
      ),
  });

  if (!routerState) {
    navigate(ERouteLinks.Dashboard);
  }

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    [legalEntityStorageAddress],
  );

  useEffect(() => {
    if (legalEntityStorageAddress && previousCoords) {
      setLegalEntityFilteredStorageAddresses(
        legalEntityStorageAddress.filter(
          (it) =>
            it.latitude !== previousCoords.latitude &&
            it.longitude !== previousCoords.longitude,
        ),
      );
    }
  }, [legalEntityStorageAddress, previousCoords]);

  const fillLastTransactionData = (data: any) => {
    setKeeperUniqueId(data.keeperId);
    setOwnerUniqueId(data.ownerId);
    setLastTransactionUniqueId(data.uniqueId);
    setPreviousCoords({ latitude: data.latitude, longitude: data.longitude });

    if (data.latitude && data.longitude) setCoords([data.latitude, data.longitude]);
  };

  useEffect(() => {
    if (routerState.assetUniqueId) {
      if (routerState.asset === Asset.FIREARM && routerState.isGroup) {
        getGroupLastTransaction(routerState.assetUniqueId).then((result) => {
          fillLastTransactionData({
            ...result,
            uniqueId: isStringEmpty(result.groupUniqueId) ? null : result.uniqueId,
          });
        });
      } else if (routerState.asset === Asset.FIREARM && !routerState.isGroup) {
        getFirearmLastTransaction(routerState.assetUniqueId, true).then((result) => {
          fillLastTransactionData(result);
        });
      } else if (routerState.asset === Asset.ANCILLARY && routerState.isGroup) {
        getAncillaryGroupLastTransaction(routerState.assetUniqueId).then((result) => {
          fillLastTransactionData({
            ...result,
            uniqueId: isStringEmpty(result.groupUniqueId) ? null : result.uniqueId,
          });
        });
      } else if (routerState.asset === Asset.ANCILLARY && !routerState.isGroup) {
        getAncillaryLastTransaction(routerState.assetUniqueId, true).then((result) => {
          fillLastTransactionData(result);
        });
      }
    }
  }, [routerState.asset, routerState.isGroup, routerState.assetUniqueId]);

  useEffect(() => {
    if (routerState && routerState.searchAddressesFor) {
      getLegalEntityStorageAddressesData(routerState.searchAddressesFor);
    }
  }, [routerState]);

  useEffect(() => {
    if (isListNotEmpty(legalEntityFilteredStorageAddresses) && !!coords) {
      const legalEntityMatchingAddress = mapLEAddressToDataSource(
        legalEntityFilteredStorageAddresses,
        parseDictionary(region, 'name'),
        parseDictionary(country, 'name'),
        parseDictionary(addressType, 'name'),
      ).find((it) => it.latitude === coords[0] && it.longitude === coords[1]);

      if (legalEntityMatchingAddress) {
        setSelectedAddress(legalEntityMatchingAddress);
      }
    }
  }, [legalEntityFilteredStorageAddresses]);

  useEffect(() => {
    getRegion();
    getAddressType();
    getCountry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  let defaultLatitude: number = parseFloat(
    process.env.REACT_APP_MAP_DEFAULT_COUNTRY_CENTER_LATITUDE
      ? process.env.REACT_APP_MAP_DEFAULT_COUNTRY_CENTER_LATITUDE
      : '0',
  );
  let defaultLongitude: number = parseFloat(
    process.env.REACT_APP_MAP_DEFAULT_COUNTRY_CENTER_LONGITUDE
      ? process.env.REACT_APP_MAP_DEFAULT_COUNTRY_CENTER_LONGITUDE
      : '0',
  );
  let defaultZoom: number = parseInt(
    process.env.REACT_APP_MAP_DEFAULT_ZOOM ? process.env.REACT_APP_MAP_DEFAULT_ZOOM : '5',
    10,
  );

  if (routerState?.coords?.length) {
    [defaultLatitude, defaultLongitude] = routerState.coords;
    defaultZoom = 14;
  }

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
  } = useForm({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver,
  });

  useEffect(() => {
    if (selectedAddress !== null) {
      setCoords([selectedAddress.latitude, selectedAddress.longitude]);
    }
  }, [selectedAddress]);

  const createTransactionBody = () => ({
    request: {
      typeUniqueId: ETransactionTypes.MOVED,
      keeperId: keeperUniqueId,
      ownerId: ownerUniqueId,
      date: formatDateTimeToIso(getValues('transactionDate')),
      ...(routerState.isGroup ? { groupUniqueId: routerState.assetUniqueId } : {}),
      ...(!routerState.isGroup && routerState.asset === Asset.FIREARM
        ? { firearmUniqueId: routerState.assetUniqueId }
        : {}),
      ...(!routerState.isGroup && routerState.asset === Asset.ANCILLARY
        ? { ancillaryUniqueId: routerState.assetUniqueId }
        : {}),
      latitude: coords[0],
      longitude: coords[1],
      address: selectedAddress!!.line1.trim(),
      city: selectedAddress!!.city.trim(),
      countryUniqueId: selectedAddress!!.countryUniqueId,
      lastTransactionUniqueId,
    },
  });

  const createTransaction = async () => {
    setCreateTransactionError('');

    try {
      const body = createTransactionBody();
      let transaction;

      setSubmitLoading(true);
      if (routerState.asset === Asset.FIREARM) {
        if (routerState.isGroup) {
          transaction = await createMiddleTransactionForGroup(body);
        } else {
          transaction = await createMiddleTransaction(body);
        }

        if (transaction) {
          setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
            text: t('addTransaction.transactionAdded.text'),
          });
        }

        navigate(
          `${ERouteLinks.ViewTransaction.replace(
            ':id',
            transaction.uniqueId,
          )}?expand=firearmDetails`,
        );
      } else {
        if (routerState.isGroup) {
          transaction = await createMiddleTransactionForAncillaryGroup(body);
        } else {
          transaction = await createAncillaryMiddleTransaction(body);
          if (transaction) {
            setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
              text: t('addTransaction.transactionAdded.text'),
            });
          }
        }

        if (transaction) {
          setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
            text: t('addTransaction.transactionAdded.text'),
          });
        }

        navigate(
          `${ERouteLinks.ViewTransaction.replace(
            ':id',
            transaction.uniqueId,
          )}?expand=ancillaryDetails`,
        );
      }
    } catch (e: any) {
      setCreateTransactionError(e?.response?.data?.message);
      setSubmitLoading(false);
    }
  };

  // eslint-disable-next-line consistent-return
  const handleSetLocation = () => {
    if (coords === null || !selectedAddress) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('movedTransaction.selectAddress.text'),
        variant: EAlertVariants.error,
      });
    }

    createTransaction();
  };

  const handleCheck = (checkedAddress: ITableDataSource) => {
    setSelectedAddress(checkedAddress.id === selectedAddress?.id ? null : checkedAddress);
    clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
  };

  return (
    <div>
      <Breadcrumbs items={routerState?.breadcrumbs || []} />
      <Box sx={{ mb: 2 }}>
        <StyledPageTitle variant="h4">{t('movedTransaction.header')}</StyledPageTitle>
      </Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        {t('movedTransaction.info.text')}
      </Typography>
      {isListNotEmpty(legalEntityFilteredStorageAddresses) &&
        (isDesktop ? (
          <Box sx={{ mb: 4, mt: 2 }}>
            <Table
              columns={MovedTransactionTableColumns({
                handleCheck,
                checkedId: selectedAddress?.id.toString() || null,
                t,
              })}
              dataSource={mapLEAddressToDataSource(
                legalEntityFilteredStorageAddresses,
                parseDictionary(region, 'name'),
                parseDictionary(country, 'name'),
                parseDictionary(addressType, 'name'),
              )}
              specificRowColor={renderRowColor}
              specificRowColorHover={renderRowColorHover}
            />
          </Box>
        ) : (
          <CardList
            items={mapLEAddressToDataSource(
              legalEntityFilteredStorageAddresses,
              parseDictionary(region, 'name'),
              parseDictionary(country, 'name'),
              parseDictionary(addressType, 'name'),
            )}
            render={(data, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <MovedTransactionAddressTableCard
                  data={data}
                  key={index}
                  isChecked={data.id === selectedAddress?.id}
                  onCheck={handleCheck}
                />
              </Box>
            )}
          />
        ))}
      {!isListNotEmpty(legalEntityFilteredStorageAddresses) && (
        <Box sx={{ mb: 2 }}>
          <Alert
            text={t('movedTransaction.noAddressOnList.message')}
            variant={EAlertVariants.warning}
          />
        </Box>
      )}
      <form onSubmit={handleSubmit(handleSetLocation)}>
        <Grid sx={{ columnGap: 3 }}>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <FormDateTimePicker
              name="transactionDate"
              label={t('addFirearm.location.section.transactionDate')}
              control={control}
              errors={errors}
              currentHour={true}
            />
          </Grid>
        </Grid>
        <LocationMap
          coords={coords !== null ? coords : [defaultLatitude, defaultLongitude]}
          dimensions={{ width: '100%', height: '600px' }}
          defaultZoom={defaultZoom}
          getCoordsCallback={(data: any) => {
            setCoords([data.lat, data.lng]);
          }}
          isOnline={isOnlineLicence()}
          pinDocked
        />
        <Grid container spacing={2} sx={{ mt: 5 }}>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <Button
              fullWidth
              type="submit"
              label={t('movedTransaction.changeLocation.button')}
              variant={EButtonVariants.contained}
              sx={{ mr: 1 }}
              onClick={handleSubmit}
              isLoading={submitLoading}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <Button
              fullWidth
              label={t('movedTransaction.cancel.button')}
              variant={EButtonVariants.outlined}
              onClick={() => {
                navigate(routerState.breadcrumbs[routerState.breadcrumbs.length - 2].route);
              }}
            />
          </Grid>
          <Grid container size={{ xs: 12 }} sx={{ mt: 1 }}>
            {createTransactionError && (
              <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                <Alert text={createTransactionError} variant={EAlertVariants.error} />
              </Grid>
            )}
            {samePageAlert && (
              <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export { MovedTransactionPage };
